import { createFileRoute, useNavigate, redirect, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/api/http';
import { authApi } from '@/api/endpoints';
import { tokenStore } from '@/lib/auth';
import { SPECIALTIES, APPROACHES } from '@/lib/specialties';
import { cn } from '@/lib/utils';
import { BRAZILIAN_STATES } from '@/lib/brazilian-states';
import { AvatarUpload } from '@/components/dashboard/avatar-upload';
import { CityAutocomplete } from '@/components/public/city-autocomplete';
import { TermsAcceptanceBlock } from '@/components/shared/terms-acceptance';

export const Route = createFileRoute('/onboarding/client')({
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === 'string' ? s.next : undefined,
  }),
  beforeLoad: () => {
    if (!tokenStore.getAccess()) throw redirect({ to: '/login' });
  },
  component: ClientOnboardingPage,
});

const schema = z.object({
  name: z.string().min(2, 'Nome muito curto').max(100),
  cpf: z.string().min(11, 'CPF é obrigatório').refine((v) => v.replace(/\D/g, '').length === 11, 'CPF inválido'),
  phone: z.string().optional(),
  whatsappOptIn: z.boolean().optional(),
  birthDate: z.string().optional(),
  gender: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  preferredModality: z.enum(['ONLINE', 'PRESENCIAL', 'HYBRID']).optional(),
  notes: z.string().max(500).optional(),
  specialtiesOfInterest: z.array(z.string()).default([]),
  preferredApproaches: z.array(z.string()).default([]),
  termsAccepted: z.boolean().optional(),
});
type FormData = z.infer<typeof schema>;

const onlyDigits = (v: string) => v.replace(/\D/g, '');
const formatCPF = (v: string) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const formatPhone = (v: string) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};
const formatCEP = (v: string) => onlyDigits(v).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');

function ClientOnboardingPage() {
  const navigate = useNavigate();
  const search = Route.useSearch() as { next?: string };

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const { data: existing } = useQuery({
    queryKey: ['my-client-profile'],
    queryFn: () => api.get('clients/me/profile').json<any>().catch(() => null),
  });

  const {
    register, handleSubmit, setValue, watch, reset, setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { specialtiesOfInterest: [], preferredApproaches: [] },
  });

  // Pre-popula em modo edição (com fallback no nome vindo do Google)
  useEffect(() => {
    const nameFromMe = me?.name ?? '';
    if (!existing) {
      // Primeira vez — só pré-preenche o nome do Google se houver
      reset({ name: nameFromMe, specialtiesOfInterest: [], preferredApproaches: [] });
      return;
    }
    reset({
      name: existing.user?.name ?? nameFromMe,
      cpf: existing.cpf ?? '',
      phone: existing.phone ?? '',
      whatsappOptIn: existing.whatsappOptIn ?? false,
      birthDate: existing.birthDate ? new Date(existing.birthDate).toISOString().slice(0, 10) : '',
      gender: existing.gender ?? '',
      city: existing.city ?? '',
      state: existing.state ?? '',
      zipCode: existing.zipCode ?? '',
      preferredModality: existing.preferredModality ?? undefined,
      notes: existing.notes ?? '',
      specialtiesOfInterest: existing.specialtiesOfInterest ?? [],
      preferredApproaches: existing.preferredApproaches ?? [],
    });
  }, [existing, me, reset]);

  const isNewUser = !existing;
  const cpf = watch('cpf') ?? '';
  const phone = watch('phone') ?? '';
  const zipCode = watch('zipCode') ?? '';
  const selectedSpecs = watch('specialtiesOfInterest') ?? [];
  const selectedApproaches = watch('preferredApproaches') ?? [];

  const submit = useMutation({
    mutationFn: (body: any) => api.post('clients/onboarding', { json: body }).json<any>(),
    onSuccess: () => {
      if (search.next) window.location.href = search.next;
      else navigate({ to: '/dashboard' });
    },
  });

  const onSubmit = async (data: FormData) => {
    if (isNewUser && !data.termsAccepted) {
      setError('termsAccepted', { message: 'Você precisa aceitar os Termos para continuar' });
      return;
    }
    if (isNewUser) {
      // Registra aceite formal (timestamp + IP + UA) ANTES do onboarding.
      // Backend bloqueia onboarding se não houver aceite na versão vigente.
      try {
        await api.post('auth/accept-terms').json<any>();
      } catch {
        setError('termsAccepted', { message: 'Falha ao registrar aceite. Tente novamente.' });
        return;
      }
    }
    submit.mutate({
      ...data,
      termsAccepted: undefined,
      cpf: data.cpf ? onlyDigits(data.cpf) : undefined,
      phone: data.phone ? onlyDigits(data.phone) : undefined,
      whatsappOptIn: data.phone ? !!data.whatsappOptIn : false,
      zipCode: data.zipCode ? onlyDigits(data.zipCode) : undefined,
      birthDate: data.birthDate ? new Date(data.birthDate).toISOString() : undefined,
    });
  };

  const currentAvatar = existing?.avatarUrl ?? me?.clientProfile?.avatarUrl ?? me?.image ?? null;

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <header className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/logo.png" alt="Nosso Ello" className="object-contain h-14 w-auto" />
        </Link>
        <Link to="/dashboard" className="text-sm text-teal-600 hover:underline">← Voltar ao painel</Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <h1 className="text-2xl font-extrabold text-slate-900">Complete seu cadastro</h1>
          <p className="text-sm text-slate-500 mt-1">
            Estas informações ficam privadas — são compartilhadas apenas com profissionais com quem você marcar sessão.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
            {/* Foto de perfil — upload S3 (mesmo fluxo do terapeuta) */}
            <div className="pb-5 border-b border-slate-100">
              <p className="font-semibold text-slate-700 mb-2">Foto de perfil</p>
              <AvatarUpload
                currentUrl={currentAvatar}
                fallbackInitial={me?.name ?? '?'}
                size={80}
                invalidateQueryKeys={[['me'], ['my-client-profile']]}
              />
            </div>

            <Field label="Nome completo" error={errors.name?.message}>
              <input
                placeholder="Como você quer ser chamado(a) no sistema"
                {...register('name')}
                className="input"
                autoComplete="name"
              />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="CPF *" error={errors.cpf?.message}>
                <input
                  placeholder="000.000.000-00"
                  value={formatCPF(cpf)}
                  onChange={(e) => setValue('cpf', e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Telefone / WhatsApp" error={errors.phone?.message}>
                <input
                  placeholder="(11) 99999-9999"
                  value={formatPhone(phone)}
                  onChange={(e) => setValue('phone', e.target.value)}
                  className="input"
                />
                <label className={`mt-2 flex items-start gap-2 text-xs ${phone ? 'text-slate-600' : 'text-slate-400'}`}>
                  <input
                    type="checkbox"
                    {...register('whatsappOptIn')}
                    disabled={!phone}
                    className="mt-0.5"
                  />
                  <span>
                    Este número é WhatsApp e aceito receber a confirmação de sessão
                    automática (às 9h do dia anterior à consulta).
                  </span>
                </label>
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Data de nascimento">
                <input type="date" {...register('birthDate')} className="input" />
              </Field>
              <Field label="Gênero (opcional)">
                <select {...register('gender')} className="input">
                  <option value="">Selecionar</option>
                  <option value="female">Mulher</option>
                  <option value="male">Homem</option>
                  <option value="nonbinary">Não-binário</option>
                  <option value="other">Outro</option>
                  <option value="prefer_not">Prefiro não dizer</option>
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="CEP">
                <input
                  placeholder="00000-000"
                  value={formatCEP(zipCode)}
                  onChange={(e) => setValue('zipCode', e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Cidade">
                <CityAutocomplete state={watch('state')} registerProps={register('city')} className="input" placeholder="São Paulo" />
              </Field>
              <Field label="UF">
                <select {...register('state')} className="input">
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Modalidade preferida">
              <select {...register('preferredModality')} className="input">
                <option value="">Selecionar</option>
                <option value="ONLINE">Online (Google Meet)</option>
                <option value="PRESENCIAL">Presencial</option>
                <option value="HYBRID">Tanto faz</option>
              </select>
            </Field>

            <Field label="Anotações pessoais (privado)">
              <textarea
                {...register('notes')}
                placeholder="Use este espaço para anotações suas sobre o que busca em uma terapia. Só você vê."
                rows={3}
                className="w-full bg-white border border-slate-200 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
              />
            </Field>

            <div className="border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-slate-900">Encontre seu match ideal</h3>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Suas preferências ajudam a calcular um score de compatibilidade pra cada profissional.
                Quanto mais campos você preencher, mais precisa fica a recomendação.
              </p>

              <div className="mb-5">
                <label className="text-sm font-medium text-slate-700 block mb-2">Áreas que mais te interessam</label>
                <ChipPicker
                  options={SPECIALTIES}
                  value={selectedSpecs}
                  onChange={(v) => setValue('specialtiesOfInterest', v)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Abordagens preferidas (opcional)</label>
                <ChipPicker
                  options={APPROACHES}
                  value={selectedApproaches}
                  onChange={(v) => setValue('preferredApproaches', v)}
                />
              </div>
            </div>

            {isNewUser && (
              <TermsAcceptanceBlock
                checked={!!watch('termsAccepted')}
                onChange={(v) => setValue('termsAccepted', v, { shouldValidate: false })}
                error={errors.termsAccepted?.message ?? null}
              />
            )}

            {submit.isError && <p className="text-sm text-red-600">Não foi possível salvar. Tente novamente.</p>}

            <button
              type="submit"
              disabled={isSubmitting || submit.isPending}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-md h-12 font-semibold transition shadow"
            >
              {submit.isPending ? 'Salvando...' : 'Concluir cadastro'}
            </button>
          </form>
        </div>
      </main>
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:40px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </label>
  );
}

function ChipPicker({
  options, value, onChange,
}: {
  options: readonly string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (opt: string) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const isOn = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={cn(
              'text-xs px-3 py-1.5 rounded-full border transition',
              isOn
                ? 'bg-teal-500 text-white border-teal-500'
                : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400 hover:text-teal-600',
            )}
          >
            {isOn && '✓ '}{opt}
          </button>
        );
      })}
    </div>
  );
}
